import { useMemo, useState } from 'react'

// UX54: Vote history page.
//
// A condensed list of every poll the signed-in user has voted on,
// filterable by choice and level. Reads directly from the already-
// derived `userVotes` map and `items` array — zero new network, zero
// new storage.

const CHOICE_FILTERS = [
  { id: 'all',    label: 'All' },
  { id: 'Yes',    label: 'Yes' },
  { id: 'No',     label: 'No' },
  { id: 'Unsure', label: 'Unsure' },
]
const LEVEL_FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'federal',   label: 'Federal' },
  { id: 'state',     label: 'State' },
  { id: 'city',      label: 'City' },
  { id: 'candidate', label: 'Candidates' },
  { id: 'user',      label: 'User' },
]

const CHOICE_EMOJI = { Yes: '👍', No: '👎', Unsure: '🤔' }

function fmtDate(ts) {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function choiceClass(choice) {
  if (choice === 'Yes') return 'history-choice yes'
  if (choice === 'No') return 'history-choice no'
  if (choice === 'Unsure') return 'history-choice unsure'
  return 'history-choice candidate'
}

export function HistoryPane({ items, userVotes, isLoggedIn, onOpenInFeed }) {
  const [choiceFilter, setChoiceFilter] = useState('all')
  const [levelFilter, setLevelFilter] = useState('all')

  // Join user's votes with the poll items. Latest vote wins.
  const rows = useMemo(() => {
    const out = []
    for (const [pollId, choice] of Object.entries(userVotes)) {
      const item = items.find((it) => it.id === pollId)
      if (!item) continue
      // kind === 'candidate' lives alongside item.level; treat as its
      // own filterable group so a user can scope to candidate picks.
      const groupKey = item.kind === 'candidate' ? 'candidate' : item.level
      out.push({ item, choice, groupKey })
    }
    out.sort((a, b) => (b.item.postedAt || 0) - (a.item.postedAt || 0))
    return out
  }, [items, userVotes])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (choiceFilter !== 'all') {
        // For candidate picks the choice is a candidate name — bucket
        // under "all" only; the Yes/No/Unsure filters only apply to
        // bill-shaped polls.
        if (!['Yes', 'No', 'Unsure'].includes(r.choice)) return false
        if (r.choice !== choiceFilter) return false
      }
      if (levelFilter !== 'all' && r.groupKey !== levelFilter) return false
      return true
    })
  }, [rows, choiceFilter, levelFilter])

  if (!isLoggedIn) {
    return (
      <div className="history-pane">
        <div className="empty-state">
          Sign in to see your voting history. Every vote you cast as an
          anonymous session is still yours — upgrade to Google in your
          account to carry it with you across devices.
        </div>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="history-pane">
        <div className="empty-state">
          You haven&rsquo;t voted on anything yet. Head to the feed and
          vote on a few polls to start a history.
        </div>
      </div>
    )
  }

  return (
    <div className="history-pane">
      <div className="history-head">
        <h2 className="history-title">
          Your voting history <span className="history-count">({rows.length})</span>
        </h2>
      </div>

      <div className="history-filters">
        <div className="history-filter-group" role="radiogroup" aria-label="Filter by choice">
          <span className="history-filter-label">Choice</span>
          {CHOICE_FILTERS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`history-filter ${choiceFilter === c.id ? 'active' : ''}`}
              onClick={() => setChoiceFilter(c.id)}
              aria-pressed={choiceFilter === c.id}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="history-filter-group" role="radiogroup" aria-label="Filter by level">
          <span className="history-filter-label">Level</span>
          {LEVEL_FILTERS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`history-filter ${levelFilter === l.id ? 'active' : ''}`}
              onClick={() => setLevelFilter(l.id)}
              aria-pressed={levelFilter === l.id}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          No votes match these filters.{' '}
          <button
            type="button"
            className="link-btn"
            onClick={() => { setChoiceFilter('all'); setLevelFilter('all') }}
          >
            Reset
          </button>
        </div>
      ) : (
        <ul className="history-list">
          {filtered.map((row) => (
            <li key={row.item.id} className="history-row">
              <button
                type="button"
                className="history-row-btn"
                onClick={() => onOpenInFeed?.(row.item.id)}
                aria-label={`Open ${row.item.title || row.item.id} in feed`}
              >
                <span className={choiceClass(row.choice)}>
                  <span aria-hidden="true">{CHOICE_EMOJI[row.choice] || '•'}</span>
                  <span>{row.choice}</span>
                </span>
                <span className="history-row-main">
                  <span className="history-row-title">
                    {row.item.title || row.item.plainSummary || 'Untitled poll'}
                  </span>
                  <span className="history-row-meta">
                    {row.item.levelLabel}
                    {row.item.billId ? ` · ${row.item.billId}` : ''}
                    {' · '}
                    {fmtDate(row.item.postedAt)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
